import React from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { colors } from '@skyslope/mache/dist/src/theme';
import { useHistory } from 'react-router-dom';
import { logoutCleanup } from '../../auth';
import useUserInfo from '../../auth/useUserInfo';
import type { LaunchDarklyFlags } from '../../common/launchDarkly';
import { FooterLinks } from '../../components/common';
import { SellersShieldPromoBanner } from '../../components/envelope-sent/SellersShieldPromoBanner';
import { useGetIntegrations } from '../../hooks/integrations/use-get-integrations';
import { useSearchParams } from '../../hooks/use-search-params';
import { useSellersShieldBanner } from '../../hooks/use-sellers-shield-banner';

import { ReactComponent as Mailbox } from '../../images/nova/mailbox.svg';
import { useGetEnvelopeAutoReminders } from '../../lib/api/envelope';
import { useShallowSelector } from '../../lib/reduxHooks';
import { UserOrigins } from '../../lib/types';
import { bold } from '../../lib/utils';

import { postMessageToPrime, MessagesToPrime } from '../../utils/post-message';
import { imDoneRedirectHref, isSmartListingUrl } from '../envelope-template-builder/navigation-utils';
import { EnvelopeSentCard } from './EnvelopeSentCard';
import { EnvelopeSentForSellersShield } from './EnvelopeSentForSellersShield';
import { useSignNow } from './use-sign-now';

const mobilePortrait = '(max-width: 599.95px)';
const mobileLandscape = '(min-width: 600px) and (max-height: 599.95px) ';
const desktop = '(min-width: 600px) and  (min-height: 600px)';

const useStyles = makeStyles(() => ({
  container: {
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'calc(100vh - 180px)',
    },
    [`@media ${desktop}`]: {
      display: 'flex',
      flexFlow: 'column',
      alignSelf: 'center',
    },
  },
  contentWrapper: {
    display: 'flex',
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      flexDirection: 'column',
      flex: 1,
    },
    [`@media ${desktop}`]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: '60px',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px 20px',
    },
  },
  mailboxContainer: {
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      paddingLeft: '32px',
      paddingRight: '32px',
    },
    [`@media ${desktop}`]: {
      flex: '0 0 auto',
      marginTop: '32px',
      marginRight: '-50px',
      paddingLeft: '20px',
    },
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      flex: 1,
      gap: '8px',
      paddingBottom: '100px',
      justifyContent: 'center',
    },
    [`@media ${desktop}`]: {
      flex: '1 1 auto',
      alignItems: 'flex-start',
      marginTop: '32px',
    },
  },
  header: {
    fontFamily: 'proxima-nova, sans-serif',
    letterSpacing: '-0.28px',
    [`@media ${mobilePortrait}`]: {
      textAlign: 'center',
      width: '75%',
      marginBottom: '24px',
      marginTop: '24px',
    },
    [`@media ${mobileLandscape}`]: {
      textAlign: 'center',
      marginBottom: '24px',
      marginTop: '-24px',
    },
    [`@media ${desktop}`]: {
      textAlign: 'left',
      marginBottom: '16px',
    },
  },
  svgContainer: {
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    [`@media ${desktop}`]: {
      width: '247px',
      height: '197px',
    },
  },
  buttonContainer: {
    display: 'flex',
    zIndex: 100,
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      position: 'fixed',
      bottom: '0px',
      left: '0px',
      width: '100%',
      justifyContent: 'space-evenly',
      paddingTop: '24px',
      paddingBottom: '24px',
      background: '#F4F8FC',
      borderTop: `1px solid ${colors.grey[400]}`,
    },
    [`@media ${desktop}`]: {
      gap: '16px',
      alignItems: 'center',
      flex: '0 0 auto',
      marginTop: '72px',
    },
  },
  textContent: {
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      textAlign: 'center',
      paddingLeft: '32px',
      paddingRight: '32px',
      maxWidth: '100%',
    },
    [`@media ${desktop}`]: {
      textAlign: 'left',
    },
  },
  footer: {
    width: 'calc(80vw)',
    bottom: '40px',
    left: '100px',
    right: '100px',
    [`@media ${mobilePortrait}, ${mobileLandscape}`]: {
      display: 'none',
    },
  },
}));

interface EnvelopeSentProps {
  flags?: LaunchDarklyFlags;
}

export function EnvelopeSent(props: EnvelopeSentProps) {
  const { flags } = props;
  const { envelopeId, config, settings, settingsFetched, documents } = useShallowSelector((state) => ({
    envelopeId: state.senderBuild.id,
    config: state.senderBuild.config,
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
    documents: state.senderBuild.documents,
  }));

  const classes = useStyles();
  const history = useHistory();
  const search = useSearchParams();
  const getAutoReminders = useGetEnvelopeAutoReminders(envelopeId);
  const userInfo = useUserInfo();

  const envelopeHasSellersShieldForms = documents.some((d) => d.metadata?.integrations?.includes('sellers-shield'));
  const integrations = useGetIntegrations({ enabled: envelopeHasSellersShieldForms });

  const [isInitialized, setIsInitialized] = React.useState(false);

  const eligibleForSellersShieldEnablement =
    integrations.data?.sellersshield?.brokerEnablement === 'active' &&
    integrations.data?.sellersshield?.userEnablement === 'inactive';

  const isStandaloneDS3 = !config.senderUi?.sendRedirectUrl;

  const { shouldShowBanner: shouldShowSellersShieldBanner } = useSellersShieldBanner({
    flags,
    integrations: integrations.data,
    showSellersShieldBanner: search.showSellersShieldBanner === true,
    isFromFilesUi: !isStandaloneDS3,
  });

  // Only mark banner as "shown" if the user actually sees it (page is not skipped)
  const hasSmartListing = isSmartListingUrl(config.senderUi?.sendRedirectUrl);
  const bannerWasActuallyShown = shouldShowSellersShieldBanner && !(search.skipSentPage && hasSmartListing);

  const {
    mutate: signNow,
    isAllowedToSignNow,
    showButton,
  } = useSignNow({
    flags,
    userInfo,
    shouldShowSellersShieldBanner: bannerWasActuallyShown,
  });

  // Sign Now is the sender signing their own row and nothing else; in-person
  // lives on the envelope list's More menu. Delegates are excluded inside
  // useSignNow, so showButton already accounts for them.
  const showSignNow = showButton && search.theme !== 'agnostic';
  const isStartSafeAccount = settingsFetched && settings?.account?.userOrigin === UserOrigins.DOP;
  const showAutomatedRemindersFlag = Boolean(flags?.['automated-reminders'] && settingsFetched && !isStartSafeAccount);

  React.useEffect(() => {
    if (isInitialized) return;
    window.scrollTo(0, 0);
    setIsInitialized(true);
  }, [isInitialized]);

  // Redirect for smart listing skip flow even when isAllowedToSignNow is false
  React.useEffect(() => {
    const hasSmartListing = isSmartListingUrl(config.senderUi?.sendRedirectUrl);
    const redirectUrl = config.senderUi?.sendRedirectUrl;
    if (search.skipSentPage && hasSmartListing && userInfo && !isAllowedToSignNow && redirectUrl) {
      if (search.theme === 'agnostic') {
        postMessageToPrime(MessagesToPrime.DIGISIGN_ENVELOPE_SENT, {
          digisignEnvelopeId: envelopeId,
        });
      }

      logoutCleanup();
      window.location.href = redirectUrl;
    }
  }, [search.skipSentPage, search.theme, config.senderUi?.sendRedirectUrl, isAllowedToSignNow, userInfo, envelopeId]);

  function handleImDone() {
    // Send message to Prime when in agnostic theme (iframe context)
    if (search.theme === 'agnostic') {
      postMessageToPrime(MessagesToPrime.DIGISIGN_ENVELOPE_SENT, {
        digisignEnvelopeId: envelopeId,
      });
    }

    if (config.senderUi?.sendRedirectUrl) {
      let redirectUrl = config.senderUi.sendRedirectUrl;

      if (bannerWasActuallyShown) {
        const url = new URL(redirectUrl);
        url.searchParams.set('sellersShieldBannerShown', 'true');
        redirectUrl = url.toString();
      }

      logoutCleanup();
      window.location.href = imDoneRedirectHref(redirectUrl);
    } else {
      history.push('/envelopes');
    }
  }

  // Skip rendering for smart listing flows to prevent page flash
  // All hooks have run, so redirect effect above can execute
  if (search.skipSentPage && hasSmartListing) {
    return null;
  }

  if (envelopeHasSellersShieldForms && integrations.isLoading) return null;

  // API call has resolved, and the user is eligible for Sellers Shield enablement
  if (eligibleForSellersShieldEnablement) {
    // showButton, not showSignNow: this variant applies no agnostic-theme test.
    return <EnvelopeSentForSellersShield onDone={handleImDone} onSignNow={signNow} showSignNowBtn={showButton} />;
  }

  if (flags?.envelopeSentRedesign) {
    return (
      <div className="flex flex-col grow w-full bg-white">
        <EnvelopeSentCard
          onDone={handleImDone}
          onSignNow={signNow}
          showSignNowBtn={showSignNow}
          showAutomatedReminders={Boolean(showAutomatedRemindersFlag && getAutoReminders.data)}
        />
        {/* Seller's Shield Promo Banner */}
        {shouldShowSellersShieldBanner && <SellersShieldPromoBanner />}
        <div className="mt-auto px-16 pb-8 max-sm:hidden">
          <FooterLinks showSocialIcons />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.contentWrapper}>
        <div className={classes.mailboxContainer}>
          <div className={classes.svgContainer}>
            <Mailbox />
          </div>
        </div>

        <div className={classes.mainContainer}>
          <Typography variant="h3" className={classes.header}>
            Envelope Sent!
          </Typography>

          <div className={classes.textContent}>
            <Typography variant="body1">Recipients receive an email when it&apos;s their turn to sign.</Typography>

            {showAutomatedRemindersFlag && getAutoReminders.data ? (
              <Typography variant="body1" style={{ marginTop: '16px' }}>
                If they don&apos;t sign within {bold('24 hours')}, we&apos;ll automatically send a reminder on your
                behalf.
              </Typography>
            ) : null}
          </div>
        </div>

        <div className={classes.buttonContainer}>
          {showSignNow ? (
            <Button color="primary" variant="contained" onClick={signNow} data-spec="sign-now-button">
              Sign now
            </Button>
          ) : null}
          <Button color="primary" variant="outlined" onClick={handleImDone} data-spec="im-done-button">
            I&apos;m done
          </Button>
        </div>
      </div>

      {/* Seller's Shield Promo Banner */}
      {shouldShowSellersShieldBanner && <SellersShieldPromoBanner />}

      <div className={classes.footer}>
        <FooterLinks showSocialIcons />
      </div>
    </div>
  );
}
